import React from 'react';
import { Package, Info, DollarSign, Warehouse, Settings, FileText } from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';

interface ItemDetailsProps {
  item: ItemsDto;
  onBack: () => void;
}

export const ItemDetails: React.FC<ItemDetailsProps> = ({ item, onBack }) => {
  const { t } = useTranslation();
  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null) return '-';
    return num.toLocaleString('no-NO', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatCurrency = (amount: number | undefined, currency: string | undefined): string => {
    if (amount === undefined || amount === null) return '-';
    return `${formatNumber(amount)} ${currency || 'NOK'}`;
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBoolean = (value: boolean | undefined): string => {
    if (value === undefined || value === null) return '-';
    return value ? t('warehouse.modules.itemAdmin.fields.yes') : t('warehouse.modules.itemAdmin.fields.no');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <BackButton onClick={onBack} label={t('warehouse.modules.itemAdmin.backToList')} />
        </div>

        <div className="flex items-start gap-3 sm:gap-4">
          <div className="bg-blue-100 rounded-full p-2 sm:p-3 flex-shrink-0">
            <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{item.itemNo}</h1>
            <p className="text-sm sm:text-base text-gray-600 break-words mt-1">{item.description1 || t('warehouse.modules.itemAdmin.fields.noDescription')}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Info className="h-5 w-5 text-gray-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.itemAdmin.sections.basicInfo')}</h2>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.itemNumber')}</label>
                <p className="text-sm text-gray-900 font-mono break-all">{item.itemNo}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.company')}</label>
                <p className="text-sm text-gray-900">{item.companyNo}</p>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.description')}</label>
              <p className="text-sm text-gray-900 break-words">{item.description1 || '-'}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.barcode')}</label>
                <p className="text-sm text-gray-900 font-mono break-all">{item.itemBarCode || '-'}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.supplierItem')}</label>
                <p className="text-sm text-gray-900 break-all">{item.supplierItemNo || '-'}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.unitCode')}</label>
                <p className="text-sm text-gray-900">{item.itemUnitCode || '-'}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.unitText')}</label>
                <p className="text-sm text-gray-900">{item.itemUnitTxt || '-'}</p>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.itemType')}</label>
              <p className="text-sm text-gray-900">{item.itemType || '-'}</p>
            </div>
          </div>
        </div>

        {/* Inventory Information */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Warehouse className="h-5 w-5 text-gray-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.itemAdmin.sections.inventoryInfo')}</h2>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.inventory')}</label>
                <p className="text-lg sm:text-xl font-semibold text-gray-900">{formatNumber(item.quantity)}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.available')}</label>
                <p className="text-lg sm:text-xl font-semibold text-green-600">{formatNumber(item.quantityAvailable)}</p>
              </div>
            </div>

            <div className="bg-orange-50 p-3 rounded-lg">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.reserved')}</label>
              <p className="text-lg sm:text-xl font-semibold text-orange-600">{formatNumber(item.quantityReserved)}</p>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.standardLocation')}</label>
              <p className="text-sm text-gray-900 break-words">{item.stdLocationName || '-'}</p>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.standardBinCode')}</label>
              <p className="text-sm text-gray-900 font-mono break-all">{item.stdBinCode || '-'}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.unitFactor')}</label>
                <p className="text-sm text-gray-900">{formatNumber(item.unitFactor)}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.reportID')}</label>
                <p className="text-sm text-gray-900">{item.reportID || '-'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Price Information */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <DollarSign className="h-5 w-5 text-gray-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.itemAdmin.sections.priceInfo')}</h2>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.purchasePrice')}</label>
                <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">{formatCurrency(item.purchasePrice, item.currency)}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.currency')}</label>
                <p className="text-sm text-gray-900">{item.currency || '-'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Settings className="h-5 w-5 text-gray-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.itemAdmin.sections.configuration')}</h2>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.stockTransactions')}</label>
                <p className="text-sm text-gray-900">{formatBoolean(item.stcTransactionsYesNo)}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.useSharedBin')}</label>
                <p className="text-sm text-gray-900">{formatBoolean(item.useSharedBinYesNo)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.newLine')}</label>
                <p className="text-sm text-gray-900">{formatBoolean(item.newLineYesNo)}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.useMaxReplenish')}</label>
                <p className="text-sm text-gray-900">{formatBoolean(item.useMaxReplenishQtyYesNo)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.binGroup')}</label>
                <p className="text-sm text-gray-900">{item.binGrpNo || '-'}</p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.pickRule')}</label>
                <p className="text-sm text-gray-900">{item.pickRuleNo || '-'}</p>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.maxReplenishQty')}</label>
              <p className="text-sm text-gray-900">{formatNumber(item.maxReplenishQty)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Groups and Categories */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <FileText className="h-5 w-5 text-gray-600 flex-shrink-0" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.itemAdmin.sections.categoriesAndGroups')}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.itemGroup1')}</label>
            <p className="text-sm text-gray-900 break-words">{item.itemGrp1 || '-'}</p>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.itemGroup2')}</label>
            <p className="text-sm text-gray-900 break-words">{item.itemGrp2 || '-'}</p>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.itemGroup3')}</label>
            <p className="text-sm text-gray-900 break-words">{item.itemGrp3 || '-'}</p>
          </div>
        </div>
      </div>

      {/* Free Fields */}
      {(item.freeTxt1 || item.freeTxt2 || item.freeTxt3 || item.freeTxt4 || item.freeTxt5) && (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <FileText className="h-5 w-5 text-gray-600 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.itemAdmin.sections.freeFields')}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {item.freeTxt1 && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.freeText1')}</label>
                <p className="text-sm text-gray-900 break-words">{item.freeTxt1}</p>
              </div>
            )}
            {item.freeTxt2 && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.freeText2')}</label>
                <p className="text-sm text-gray-900 break-words">{item.freeTxt2}</p>
              </div>
            )}
            {item.freeTxt3 && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.freeText3')}</label>
                <p className="text-sm text-gray-900 break-words">{item.freeTxt3}</p>
              </div>
            )}
            {item.freeTxt4 && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.freeText4')}</label>
                <p className="text-sm text-gray-900 break-words">{item.freeTxt4}</p>
              </div>
            )}
            {item.freeTxt5 && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.freeText5')}</label>
                <p className="text-sm text-gray-900 break-words">{item.freeTxt5}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Information */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <Info className="h-5 w-5 text-gray-600 flex-shrink-0" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('warehouse.modules.itemAdmin.sections.auditInfo')}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.createdBy')}</label>
            <p className="text-sm text-gray-900 break-words">{item.createdBy || '-'}</p>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.createdDate')}</label>
            <p className="text-sm text-gray-900">{formatDate(item.created)}</p>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.updatedBy')}</label>
            <p className="text-sm text-gray-900 break-words">{item.updatedBy || '-'}</p>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('warehouse.modules.itemAdmin.fields.updatedDate')}</label>
            <p className="text-sm text-gray-900">{formatDate(item.lastUpdate)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
